import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { buildMenu, NavLink } from '../shell/nav-menu';
import { ShellService } from '../shell/shell.service';

interface ReportGroup {
  title: string;
  reports: NavLink[];
}

const DEFAULT_QUICK_LINKS = ['create-inspection'];

// Report Library (mockup reports): every report this user can open, as cards, with a searchable
// side list grouped like the sidebar. The list is the sidebar's own Reports menu (nav-menu.ts),
// so names, links and who sees what (Agent Activity: administrators) stay in one place.
// Pins are the sidebar's Quick Links (user preference 'quick_links').
@Component({
  selector: 'app-report-library',
  templateUrl: './report-library.component.html',
  styleUrls: ['./report-library.component.scss']
})
export class ReportLibraryComponent implements OnInit, OnDestroy {
  groups: ReportGroup[] = [];
  search = '';
  openGroup = '';
  // The report whose Help & training dialog is open (mockup report-training-dialog).
  training: NavLink = null;
  private pinned: string[] = [];
  private subscriptions: Subscription[] = [];
  private collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });

  constructor(private shell: ShellService) {}

  ngOnInit() {
    const user = this.shell.user || {};
    const menu = buildMenu({
      isInspector: user.user_level == 'inspector',
      isAdministrator: user.user_level == 'administrator',
      inspectionTabOrder: undefined,
      apiSite: environment.apiSite,
      oldSite: environment.oldSite
    });
    const reports = menu.filter(entry => entry.id === 'reports')[0];
    this.groups = ((reports && reports.sections) || [])
      .map(section => ({
        title: section.title,
        reports: section.links.slice().sort((a, b) => this.collator.compare(a.label, b.label))
      }))
      .sort((a, b) => this.collator.compare(a.title, b.title));
    this.openGroup = this.groups.length ? this.groups[0].title : '';

    this.subscriptions.push(this.shell.preferences$.subscribe(() => {
      const pins = this.shell.get('quick_links', DEFAULT_QUICK_LINKS);
      this.pinned = Array.isArray(pins) ? pins : DEFAULT_QUICK_LINKS;
    }));
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  @HostListener('document:keydown.escape')
  closeTraining() {
    this.training = null;
  }

  private matches(report: NavLink): boolean {
    const q = this.search.trim().toLowerCase();
    return !q || report.label.toLowerCase().indexOf(q) !== -1;
  }

  // Cards: every visible report, alphabetical, with its group.
  get cards(): { report: NavLink, group: string }[] {
    const all = [];
    this.groups.forEach(g => g.reports.filter(r => this.matches(r)).forEach(r => all.push({ report: r, group: g.title })));
    return all.sort((a, b) => this.collator.compare(a.report.label, b.report.label));
  }

  visibleReports(group: ReportGroup): NavLink[] {
    return group.reports.filter(r => this.matches(r));
  }

  // While searching every group with a match is open; otherwise one at a time.
  isOpen(group: ReportGroup): boolean {
    return this.search.trim() ? this.visibleReports(group).length > 0 : this.openGroup === group.title;
  }

  toggleGroup(group: ReportGroup) {
    this.openGroup = this.openGroup === group.title ? '' : group.title;
  }

  isPinned(report: NavLink): boolean {
    return this.pinned.indexOf(report.id) !== -1;
  }

  togglePin(report: NavLink) {
    const pins = this.isPinned(report) ? this.pinned.filter(id => id !== report.id) : this.pinned.concat(report.id);
    this.shell.set('quick_links', pins);   // same preference the sidebar's Quick Links use
  }

  trackById(index: number, item: { report: NavLink }) {
    return item.report.id;
  }
}
